import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const sharedData: Pick<LocDef, 'htmlAllowed' | 'shared' | 'portal' | 'feature'> = {
	feature: HubFeature.Core,
	htmlAllowed: false,
	shared: false,
	portal: 'SP',
};

export type SPReassignCandidateDataLocs =
	| 'SPReassignCandidateDataTabTitle'
	| 'SPReassignCandidateDataPrimaryTitle'
	| 'SPReassignCandidateDataSecondaryTitle'
	| 'SPReassignCandidateDataPrimaryButton'
	| 'SPReassignCandidateDataSecondaryButton'
	| 'SPReassignCandidateDataNoSelection'
	| 'SPReassignCandidateDataSubmitAction'
	| 'SPReassignCandidateDataSwapAction'
	| 'SPReassignCandidateDataInvalidSelection'
	| 'SPReassignCandidateDataAssignmentInstructions'
	| 'SPReassignCandidateDataSearchInstructions';

export const spReassignCandidateDataLocs: LocDefs<SPReassignCandidateDataLocs> = {
	SPReassignCandidateDataTabTitle: {
		defaultValue: 'Search | Reassign Candidate Data',
		description: 'Tab title for the reassign candidate data search page.',
		legacyName: 'Core/Tools.ReassignCandidateData.Title.SearchTabTitle',
		displayPath: 'Tools|Reassign Candidate Data|Search|Tab Title',
		displayType: 'Title',
		...sharedData,
	},
	SPReassignCandidateDataPrimaryTitle: {
		defaultValue: 'Winning Candidate',
		description: 'Label used to denote the winning candidate.',
		legacyName: 'Core/Tools.ReassignCandidateData.Label.WinningCandidateLabel',
		displayPath: 'Tools|Reassign Candidate Data|Search|Winning Candidate Label',
		displayType: 'Title',
		...sharedData,
	},
	SPReassignCandidateDataSecondaryTitle: {
		defaultValue: 'Losing Candidate',
		description: 'Label used to denote the losing candidate.',
		legacyName: 'Core/Tools.ReassignCandidateData.Label.LosingCandidateLabel',
		displayPath: 'Tools|Reassign Candidate Data|Search|Losing Candidate Label',
		displayType: 'Title',
		...sharedData,
	},
	SPReassignCandidateDataPrimaryButton: {
		defaultValue: 'Winner',
		description: 'Button used to select the winning candidate.',
		legacyName: 'Core/Tools.ReassignCandidateData.Button.WinningCandidateAction',
		displayPath: 'Tools|Reassign Candidate Data|Search|Winner Selection Button',
		displayType: 'Button',
		...sharedData,
	},
	SPReassignCandidateDataSecondaryButton: {
		defaultValue: 'Loser',
		description: 'Button used to select the losing candidate.',
		legacyName: 'Core/Tools.ReassignCandidateData.Button.LosingCandidateAction',
		displayPath: 'Tools|Reassign Candidate Data|Search|Loser Selection Button',
		displayType: 'Button',
		...sharedData,
	},
	SPReassignCandidateDataNoSelection: {
		defaultValue: 'No candidate selected',
		description: 'Message displayed when no candidate is selected.',
		legacyName: 'Core/Tools.ReassignCandidateData.Content.NoCandidateSelected',
		displayPath: 'Tools|Reassign Candidate Data|Search|No Candidate Selected',
		displayType: 'Content',
		...sharedData,
	},
	SPReassignCandidateDataSubmitAction: {
		defaultValue: 'Reassign Candidate Data',
		description: 'Button used to continue to select data for reassignment.',
		legacyName: 'Core/Tools.ReassignCandidateData.Button.ReassignCandidateData',
		displayPath: 'Tools|Reassign Candidate Data|Search|Continue Button',
		displayType: 'Button',
		...sharedData,
	},
	SPReassignCandidateDataSwapAction: {
		defaultValue: 'Swap',
		description: 'Button used to swap the selected candidates.',
		legacyName: 'Core/Tools.ReassignCandidateData.Button.Swap',
		displayPath: 'Tools|Reassign Candidate Data|Search|Swap Button',
		displayType: 'Button',
		...sharedData,
	},
	SPReassignCandidateDataInvalidSelection: {
		defaultValue: 'Cannot select the same candidate',
		description: 'Message displayed when the user attempts to select the same candidate twice.',
		displayPath: 'Tools|Reassign Candidate Data|Search|Duplicate Candidates',
		displayType: 'Content',
		...sharedData,
	},
	SPReassignCandidateDataAssignmentInstructions: {
		defaultValue: 'Select candidates to reassign below',
		description:
			'Instructions displayed after candidates are found, but before they are selected.',
		displayPath: 'Tools|Reassign Candidate Data|Search|Reassignment Instructions',
		legacyName: 'Core/Tools.ReassignCandidateData.Content.AssignInstructions',
		displayType: 'Content',
		feature: HubFeature.Core,
		htmlAllowed: true,
		shared: false,
		portal: 'SP',
	},
	SPReassignCandidateDataSearchInstructions: {
		defaultValue: 'Use the search above to find candidates to reassign',
		description: 'Instructions displayed before a search is performed.',
		displayPath: 'Tools|Reassign Candidate Data|Search|Search Instructions',
		displayType: 'Content',
		legacyName: 'Core/Tools.ReassignCandidateData.Content.SearchInstructions',
		feature: HubFeature.Core,
		htmlAllowed: true,
		shared: false,
		portal: 'SP',
	},
};

export type SPReassignCandidateDataLocKeys = LocMap<SPReassignCandidateDataLocs>;

export const spReassignCandidateDataLocKeys = MapLocKeys<SPReassignCandidateDataLocs>(
	spReassignCandidateDataLocs
);
